import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OrderProtectionFormShape } from './smart-cart-form/widget-editors/order-protection/order-protection.model';
import { of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Shop } from '@app/services/shops/shop.model';
import { ShopService } from '@app/services/shops/shop.service';
import { SmartCartFormComponent } from './smart-cart-form/smart-cart-form.component';
import { StandaloneWidgetFormComponent } from './standalone-widgets/standalone-widget-form/standalone-widget-form.component';
import { API_TYPE_TO_BLOCK_TYPE } from './smart-cart-form/smart-cart-block.model';
import {
  PreviewSectionId,
  PreviewWidget,
  SmartCartPreviewConfig,
} from './smart-cart-form/preview/widget-previews/widget-preview.types';
import { SmartCartListItem, SmartCartService } from './smart-cart.service';

export type SmartCartPageMode = 'smart-carts' | 'standalone';

@Component({
  selector: 'smart-cart',
  templateUrl: './smart-cart.component.html',
  styleUrls: ['./smart-cart.component.scss'],
  standalone: false,
})
export class SmartCartComponent implements OnInit {
  @ViewChild(SmartCartFormComponent) smartCartForm?: SmartCartFormComponent;
  @ViewChild(StandaloneWidgetFormComponent) standaloneForm?: StandaloneWidgetFormComponent;

  private readonly destroyRef = inject(DestroyRef);

  mode: SmartCartPageMode = 'smart-carts';
  showList = true;
  showPreviewInShopify = false;
  liveCartLoading = false;
  liveCartPreviewConfig: SmartCartPreviewConfig | null = null;
  // Standalone live-preview form, pushed up from the child form. A plain field (not the ViewChild getter)
  // so the preview binding is stable across change-detection.
  standalonePreviewForm: FormGroup<OrderProtectionFormShape> | null = null;
  // Pending macrotask from onStandaloneFormReady — tracked so navigation can cancel it before it restores
  // a stale form over a cleared/newer preview.
  private standalonePreviewTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shopService: ShopService,
    private smartCartService: SmartCartService,
  ) {}

  ngOnInit(): void {
    this.checkRoute();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.checkRoute());
  }

  onNew(): void {
    this.router.navigate([this.mode === 'standalone' ? '/engage/smart-cart/standalone/new' : '/engage/smart-cart/new']);
  }

  onCancel(): void {
    this.router.navigate([this.mode === 'standalone' ? '/engage/smart-cart/standalone' : '/engage/smart-cart']);
  }

  onSave(): void {
    if (this.mode === 'standalone') {
      this.standaloneForm?.save();
    } else {
      this.smartCartForm?.save();
    }
  }

  onTabChange(mode: string): void {
    if (mode === this.mode) return;
    this.router.navigate([mode === 'standalone' ? '/engage/smart-cart/standalone' : '/engage/smart-cart']);
  }

  // The standalone form emits its ready form here; set on a macrotask so the parent's preview binding
  // never changes mid change-detection (avoids NG0100 from reading the child's state during CD).
  onStandaloneFormReady(form: FormGroup<OrderProtectionFormShape>): void {
    clearTimeout(this.standalonePreviewTimer);
    this.standalonePreviewTimer = setTimeout(() => (this.standalonePreviewForm = form));
  }

  get saveDisabled(): boolean {
    return this.mode === 'standalone'
      ? !!this.standaloneForm?.formGroup?.invalid
      : !!this.smartCartForm?.formGroup?.invalid;
  }

  get saving(): boolean {
    return this.mode === 'standalone' ? !!this.standaloneForm?.saving : !!this.smartCartForm?.saving;
  }

  get isEditingBlock(): boolean {
    return this.mode === 'smart-carts' && !!this.smartCartForm?.editingBlock;
  }

  // Standalone new/edit form: the "‹ Back  Order Protection" header comes from the form's LayoutService,
  // so this drives the header to show only a Save action (Back handles cancel).
  get isStandaloneForm(): boolean {
    return this.mode === 'standalone' && !this.showList;
  }

  onLiveStateChanged(): void {
    this.refreshLiveCartAvailability();
  }

  onPreviewInShopify(): void {
    this.shopService.shop$
      .pipe(
        filter((shop): shop is Shop => !!shop?.id && !!shop?.name),
        take(1),
        switchMap((shop) => {
          if (!this.showList) {
            const rawId = this.route.snapshot.params['id'];
            const cartId = rawId && !isNaN(+rawId) ? Number(rawId) : null;
            return of({ shop, cartId });
          }
          // List view → find the active cart so the merchant lands on the currently-live config.
          return this.smartCartService
            .list()
            .pipe(map((carts) => ({ shop, cartId: carts?.find((c) => c.isLive)?.id ?? null })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ shop, cartId }) => {
        const url = cartId ? `https://${shop.name}/?testSmartCart=${cartId}` : `https://${shop.name}/`;
        window.open(url, '_blank');
      });
  }

  private checkRoute(): void {
    const url = this.router.url.split('?')[0];
    this.mode = url.includes('/smart-cart/standalone') ? 'standalone' : 'smart-carts';

    const id = this.route.snapshot.params['id'];
    const hasId = !!id && !isNaN(+id);
    this.showList = !url.includes('/new') && !hasId;

    // Clear the standalone preview whenever we're not on a standalone form (list view or Smart Carts tab);
    // the child re-emits its form when a form is opened.
    if (this.mode !== 'standalone' || this.showList) {
      // Cancel any pending set-form macrotask so it can't restore a stale preview after this clear.
      clearTimeout(this.standalonePreviewTimer);
      this.standalonePreviewForm = null;
    }

    // The Shopify drawer preview only applies to full Smart Carts, never the standalone tab.
    if (this.mode === 'standalone') {
      this.showPreviewInShopify = false;
      this.liveCartPreviewConfig = null;
      return;
    }
    if (!this.showList) {
      // Only an existing (saved) cart gets "Preview In Shopify"; a new/unsaved cart (/new) does not —
      // preserves the pre-refactor behavior where /smart-cart/new forced showPreviewInShopify=false.
      this.showPreviewInShopify = hasId;
    } else {
      this.refreshLiveCartAvailability();
    }
  }

  private refreshLiveCartAvailability(): void {
    this.showPreviewInShopify = false;
    this.liveCartPreviewConfig = null;
    this.liveCartLoading = true;
    this.smartCartService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (carts) => {
          const liveCart = carts?.find((c) => c.isLive) ?? null;
          this.showPreviewInShopify = !!liveCart;
          this.liveCartPreviewConfig = liveCart ? this.buildPreviewConfigFromCart(liveCart) : null;
          this.liveCartLoading = false;
        },
        error: () => {
          this.liveCartPreviewConfig = null;
          this.liveCartLoading = false;
        },
      });
  }

  // API widget types are snake_case; the dispatcher @switches on the kebab-case BlockType enum.
  private buildPreviewConfigFromCart(cart: SmartCartListItem): SmartCartPreviewConfig {
    const toSection = (widgets: SmartCartListItem['header']): PreviewWidget[] =>
      [...widgets]
        .sort((a, b) => a.order - b.order)
        .map((widget): PreviewWidget | null => {
          const type = API_TYPE_TO_BLOCK_TYPE[widget.type];
          if (!type) return null;
          return {
            id: `live-${widget.id}`,
            type,
            enabled: widget.enabled,
            settings: widget.settings,
          };
        })
        .filter((w): w is PreviewWidget => w !== null);

    return {
      global: cart.global ?? {},
      sections: {
        header: toSection(cart.header ?? []),
        body: toSection(cart.body ?? []),
        footer: toSection(cart.footer ?? []),
      } as Record<PreviewSectionId, PreviewWidget[]>,
    };
  }
}
